import { Link } from 'expo-router';
import type { Href } from 'expo-router';
import { Image } from 'expo-image';
import { Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { type ReactNode } from 'react';

import {
  DOWNLOAD_ANDROID_URL,
  DOWNLOAD_IOS_URL,
  DOWNLOAD_LINK_URL,
} from '@/constants';
import { isMobileWebDevice } from '@/utils/isMobileWebDevice';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import DefaultBackground from '@/components/DefaultBackground';
import Logo from '@/components/Logo';

/**
 * Two ways to get someone into the app. Hardcoded — flip it by hand.
 *
 *  'smart-link'   One button pointing at the dub.co link, which opens the app
 *                 through its Universal Link if it's installed and falls through
 *                 to the right store if it isn't. One tap, no wrong choices.
 *
 *  'store-badges' The two official store badges. More familiar, and it looks
 *                 like every other "get our app" page — but it always shows a
 *                 store, even to someone who already has the app installed.
 */
const VARIATION: 'smart-link' | 'store-badges' = 'store-badges';

const SUPPORT_EMAIL = 'tv@hinote.com';

// Prefill the user agent. Detection is a heuristic, so someone will eventually
// see this screen on a desktop — and their report is only actionable if we know
// which device we misread. Asking them to dig that out themselves gets us a
// mail that says "it doesn't work" and nothing else.
function buildSupportHref(): string {
  const userAgent =
    typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent;
  const subject = 'HiNote Life TV on the web';
  const body = `\n\n---\nDevice: ${userAgent}`;

  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
}

/**
 * A bare Link renders a Text carrying `href`, which react-native-web turns into
 * a real <a> that navigates in the SAME tab — Universal Links only hand off to
 * the installed app on a direct navigation, not a window.open() popup.
 *
 * Deliberately not `asChild` + Pressable here: that path goes through Radix's
 * Slot, which merges styles by object-spreading them, and a Pressable's function
 * style spreads to nothing — silently dropping the button's background.
 */
function SmartLinkButton() {
  return (
    <Link href={DOWNLOAD_LINK_URL as Href} style={styles.button}>
      Continue in the app
    </Link>
  );
}

// Intrinsic sizes of the two badge artworks. They are NOT the same shape, so
// only one dimension can ever be shared between them without distortion — hence
// the two layouts below.
const APP_STORE_RATIO = 512 / 172;
const GOOGLE_PLAY_RATIO = 478 / 142;

// Layout constants, shared between the stylesheet and the fits-in-a-row math so
// the two can't drift apart.
const PAGE_PADDING = 24;
const CARD_MAX_WIDTH = 420;
const CARD_PADDING = 24;
const BADGE_GAP = 12;
// The row height is not fixed — it's derived from the width the card actually
// has, so the badges fill it. A fixed height forces a choice nobody wins:
// tall enough to look right on a roomy card is too wide for a small phone, and
// small enough for the phone looks stingy everywhere else.
const BADGE_MIN_ROW_HEIGHT = 48; // below this, stack instead of shrinking further
const BADGE_MAX_ROW_HEIGHT = 52; // above this they just look oversized
// Only reached below the min row height (~320pt phones). Kept modest: at 200 the
// App Store badge came out 67pt tall, which dwarfed the button it replaces.
const BADGE_STACKED_WIDTH = 160;

// Two badges side by side occupy `height * (ratioA + ratioB) + gap`, so the
// height a given width affords is that, solved for height.
const BADGE_RATIO_SUM = APP_STORE_RATIO + GOOGLE_PLAY_RATIO;

type BadgeLayout =
  | { inARow: true; rowHeight: number }
  | { inARow: false; rowHeight?: undefined };

/**
 * Side by side the badges share a HEIGHT; stacked they share a WIDTH. Either
 * way `aspectRatio` derives the other dimension, so neither badge is ever
 * squashed — the two artworks are different shapes (512x172 vs 478x142), and
 * both stores' brand guidelines forbid distorting them.
 *
 * We pick the layout from the available width rather than using `flexWrap`,
 * because flexbox gives you no way to ask whether it wrapped — and without
 * knowing that, we couldn't switch which dimension is shared.
 */
function useBadgeLayout(): BadgeLayout {
  const { width } = useWindowDimensions();
  const cardInnerWidth =
    Math.min(CARD_MAX_WIDTH, width - PAGE_PADDING * 2) - CARD_PADDING * 2;

  const heightThatFills = (cardInnerWidth - BADGE_GAP) / BADGE_RATIO_SUM;

  if (heightThatFills < BADGE_MIN_ROW_HEIGHT) {
    return { inARow: false };
  }

  return {
    inARow: true,
    rowHeight: Math.min(heightThatFills, BADGE_MAX_ROW_HEIGHT),
  };
}

/**
 * `asChild` IS safe here: the Pressable carries no style prop for Slot's
 * object-spread merge to mangle. react-native-web renders a View with an `href`
 * as an <a>, so each badge is a real link.
 */
function StoreBadges() {
  const { inARow, rowHeight } = useBadgeLayout();

  const sizeFor = (aspectRatio: number) =>
    inARow
      ? { height: rowHeight, aspectRatio }
      : { width: BADGE_STACKED_WIDTH, aspectRatio };

  return (
    <View style={[styles.badges, inARow ? styles.badgesRow : undefined]}>
      <Link href={DOWNLOAD_IOS_URL as Href} asChild>
        <Pressable>
          <Image
            source={require('@/assets/images/store-badges/appstore-download-badge.png')}
            style={sizeFor(APP_STORE_RATIO)}
            contentFit="contain"
            alt="Download on the App Store"
          />
        </Pressable>
      </Link>

      <Link href={DOWNLOAD_ANDROID_URL as Href} asChild>
        <Pressable>
          <Image
            source={require('@/assets/images/store-badges/googleplay-download-badge.png')}
            style={sizeFor(GOOGLE_PLAY_RATIO)}
            contentFit="contain"
            alt="Get it on Google Play"
          />
        </Pressable>
      </Link>
    </View>
  );
}

/**
 * Phones and tablets on the web get sent to the HiNote Life mobile app instead
 * of the TV UI, which is built around a remote and a focus engine and is a poor
 * fit for a touchscreen. This is a nudge, not a lock: nothing here gates content
 * server-side, it just points people at the better app.
 */
function DownloadPrompt() {
  return (
    <ThemedView style={styles.container}>
      <DefaultBackground />

      <View style={styles.content}>
        <Logo height={32} scaleProportionally />

        <View style={styles.card}>
          <ThemedText style={styles.title}>
            Continue in the HiNote Life app
          </ThemedText>
          <ThemedText style={styles.subtitle}>
            This is our TV experience, made for the big screen.
          </ThemedText>
          <ThemedText style={[styles.subtitle, styles.subtitleLast]}>
            On a phone or tablet, the app is the better way to work out.
          </ThemedText>

          {VARIATION === 'store-badges' ? <StoreBadges /> : <SmartLinkButton />}
        </View>

        {/* Below the card and quieter: this is the way out for anyone the
            detection got wrong, not a second thing to choose between. */}
        <ThemedText style={styles.footnote}>
          Seeing this on a computer, or have feedback? Email{' '}
          <Link href={buildSupportHref() as Href} style={styles.footnoteEmail}>
            {SUPPORT_EMAIL}
          </Link>
        </ThemedText>
      </View>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  // Sized for a phone held in the hand — this is the only screen in the app
  // that ever renders on one, so it doesn't use the TV type scale.
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 32,
    paddingHorizontal: PAGE_PADDING,
  },
  card: {
    alignItems: 'center',
    // `alignSelf: 'stretch'` + maxWidth would pin the card to the left edge on a
    // wide window: stretch fills the cross axis, then maxWidth clamps the result
    // back down from the start. Centering explicitly keeps it under the logo.
    alignSelf: 'center',
    width: '100%',
    maxWidth: CARD_MAX_WIDTH,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: 20,
    paddingVertical: 32,
    paddingHorizontal: CARD_PADDING,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  title: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 'bold',
    // Larger than the 12 between the body paragraphs, so the title reads as a
    // heading rather than as a first paragraph.
    marginBottom: 16,
    textAlign: 'center',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: 15,
    marginBottom: 12,
    textAlign: 'center',
  },
  // Wider gap under the last paragraph, to separate the copy from the button.
  subtitleLast: {
    marginBottom: 24,
  },
  // The button is an <a> on web, so it's styled as text: a flex item, which
  // blockifies the anchor's default `display: inline` and lets the padding and
  // background actually render as a pill.
  button: {
    alignSelf: 'stretch',
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: '#fff',
    fontSize: 17,
    fontWeight: '600',
    textAlign: 'center',
    textDecorationLine: 'none',
  },
  // Stacked by default (equal widths); switched to a row (equal heights) by
  // useBadgesFitInARow. The badges size themselves — see sizeFor().
  badges: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: BADGE_GAP,
  },
  badgesRow: {
    flexDirection: 'row',
  },
  footnote: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: 13,
    lineHeight: 20,
    maxWidth: CARD_MAX_WIDTH,
    textAlign: 'center',
  },
  footnoteEmail: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
    textDecorationLine: 'none',
  },
});

export default function MobileGate({ children }: { children: ReactNode }) {
  if (isMobileWebDevice) {
    return <DownloadPrompt />;
  }

  return <>{children}</>;
}
